import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components';
import {
  PersonalizationTabContent,
  ResidentsListPanel,
  useCommunityContext,
  PersonalizationSummaryOverviewTabContent,
} from '@/views';
import { enumObject } from '@/utils';
import { useGetHomeDetailsQuery } from '@/redux';
import { CommunityPermissions } from '@/core';
import { HomeAttachmentsTabContent } from './HomeAttachmentsTabContent';
import { HomeBaselineTabPanel } from '../HomeBaseline.Details';

const TAB_KEYS = [
  'residents',
  'personalization',
  'personalizationSummary',
  'documents',
  'baseline',
] as const;
export const HomeDetailsTabKeys = enumObject('HomeDetailsTabKeys', TAB_KEYS);

const { TabPane } = Tabs;

interface Props {
  id: string;
  className?: string;
}

export const HomeDetailsTabsPanel = (props: Props) => {
  const { className, id } = props;
  const { data, isFetching } = useGetHomeDetailsQuery({ id });
  const { t } = useTranslation();

  const { hasPermission } = useCommunityContext();

  if (!data || isFetching) {
    return null;
  }
  const { communityId, projectId } = data;
  return (
    <Tabs
      className={className}
      tabKeys={HomeDetailsTabKeys}
      destroyInactiveTabPane
    >
      {hasPermission(CommunityPermissions.Residents.View) && (
        <TabPane
          tab={t('homes.details.tabs.residents')}
          key={HomeDetailsTabKeys.residents}
        >
          <ResidentsListPanel
            homeId={id}
            projectId={projectId}
            communityId={communityId!}
          />
        </TabPane>
      )}

      {data.personalizationHistory.length > 0 &&
        hasPermission(CommunityPermissions.Personalization.View) && (
          <TabPane
            tab={t('homes.details.tabs.personalization')}
            key={HomeDetailsTabKeys.personalization}
          >
            <PersonalizationTabContent
              personalizationHistory={data.personalizationHistory}
              homeId={id}
              communityId={data.communityId}
              isHomeDetails={true}
            />
          </TabPane>
        )}

      {data.personalizationHistory.length > 0 && (
        <TabPane
          tab={t('homes.details.tabs.personalizationSummary')}
          key={HomeDetailsTabKeys.personalizationSummary}
        >
          <PersonalizationSummaryOverviewTabContent
            personalizationHistory={data.personalizationHistory}
            homeId={id}
            communityId={data.communityId}
          />
        </TabPane>
      )}
      {data.personalizationHistory.length > 0 && (
        <TabPane
          tab={t('homes.details.tabs.baseline')}
          key={HomeDetailsTabKeys.baseline}
        >
          <HomeBaselineTabPanel homeId={id} />
        </TabPane>
      )}

      {hasPermission(CommunityPermissions.HomeDocuments.View) && (
        <TabPane
          tab={t('homes.details.tabs.documents')}
          key={HomeDetailsTabKeys.documents}
        >
          <HomeAttachmentsTabContent
            id={id}
            communityId={communityId}
            floorplanId={data.floorplan?.id}
            projectId={data.projectId}
          />
        </TabPane>
      )}
    </Tabs>
  );
};
